import {FormSectionHeading, Typography} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {FormShell} from '@/components';
import {useFatcaCrsSupplementaryForm} from '@/features/trust-accounts/activation/packages/compliance/hooks';
import {toSellerBeneficiarySnapshots} from '@/features/trust-accounts/activation/packages/compliance/utils';
import {
  FatcaCrsBeneficiaryTable,
  FatcaCrsSupplementarySection,
  isFatcaCrsDeclarationSigned
} from '@/features/trust-accounts/fatca-crs';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {SupplementaryGuard} from './SupplementaryGuard';

import type {FormStateSnapshot} from '@/hooks';
import type {FatcaCrsDeclarationDTO, TrustSellerDetailsDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface FatcaCrsSupplementaryStepFormProps {
  trustAccountId: string;
  sellers: TrustSellerDetailsDTO[] | null | undefined;
  latestMainDeclaration: FatcaCrsDeclarationDTO | null | undefined;
  latestSupplementaryDeclaration: FatcaCrsDeclarationDTO | null | undefined;
  isLoading: boolean;
  isError: boolean;
  projectName?: string;
  lawyerName?: string;
  onFormStateChange?: (state: FormStateSnapshot) => void;
  onGoToSellerStep?: () => void;
  onGoToFatcaCrsStep?: () => void;
}

export const FatcaCrsSupplementaryStepForm: FC<FatcaCrsSupplementaryStepFormProps> = ({
  trustAccountId,
  sellers,
  latestMainDeclaration,
  latestSupplementaryDeclaration,
  isLoading,
  isError,
  projectName,
  lawyerName,
  onFormStateChange,
  onGoToSellerStep,
  onGoToFatcaCrsStep
}) => {
  const {t} = useTranslation('trustAccounts');
  const {sellerSideRole} = useTrustTypeStrategy();

  const isMainDeclarationSigned = isFatcaCrsDeclarationSigned(latestMainDeclaration?.status);
  const isSupplementarySigned = isFatcaCrsDeclarationSigned(latestSupplementaryDeclaration?.status);

  const {form, handleSave, isLocked, saveError} = useFatcaCrsSupplementaryForm({
    trustAccountId,
    sellers,
    isMainDeclarationSigned,
    latestSupplementaryDeclaration,
    isLoading,
    isError,
    onFormStateChange
  });
  const {control, setValue} = form;

  const heading = t('activation.packages.compliance.supplementary.title', {
    projectName: projectName ?? ''
  });

  const sellerRows = toSellerBeneficiarySnapshots(sellers, sellerSideRole);

  const section = (
    <FatcaCrsSupplementarySection
      mainDeclaration={latestMainDeclaration}
      lawyerName={lawyerName}
      control={control}
      setValue={setValue}
      disabled={isSupplementarySigned}
    >
      {sellerRows.length > 0 && (
        <div className="flex flex-col gap-3">
          <FormSectionHeading
            title={t('activation.packages.compliance.supplementary.partyDetails.heading')}
            description={t(
              'activation.packages.compliance.supplementary.partyDetails.supportingText'
            )}
          />
          <FatcaCrsBeneficiaryTable rows={sellerRows} />
        </div>
      )}
    </FatcaCrsSupplementarySection>
  );

  const headingElement = (
    <Typography as="h2" size="xl" weight="semibold" className="whitespace-normal">
      {heading}
    </Typography>
  );

  if (isLocked) {
    return (
      <div className="flex flex-col gap-6" data-slot="supplementary-locked">
        {headingElement}
        <SupplementaryGuard
          isMainDeclarationSigned={isMainDeclarationSigned}
          onGoToFatcaCrsStep={onGoToFatcaCrsStep}
          onGoToSellerStep={onGoToSellerStep}
        />
        <div inert className="opacity-40">
          {section}
        </div>
      </div>
    );
  }

  return (
    <FormShell
      id="fatca-crs-supplementary-form"
      className="flex flex-col gap-6"
      onSubmit={handleSave}
      saveError={saveError}
    >
      {headingElement}
      {section}
    </FormShell>
  );
};
